import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuth } from "../utils/auth";
import { CITY_LIST_AP, CITY_LIST_TG, canonicalCity, citiesMatching, apOnlyCities, CITIES_EVENT } from "../utils/cities";
import { localCity as cityIn } from "../utils/localNames";
import { useLanguage } from "../context/LanguageContext";
import { usePlatformConfig } from "../utils/platformConfig";
import { Icon } from "./Icon";
import { CITY_PICKER_EVENT } from "./home/HomeAtoms";
import { loadGoogleMaps } from "../utils/googleMaps";


/* The five the app is translated into, each in its own script - a Telugu speaker looks
   for "తెలుగు", not "Telugu". The button shows the short form, which fits beside the
   city on a 360px phone. */
const HEADER_LANGS = [
  { code: "en", native: "English", abbr: "En" },
  { code: "te", native: "తెలుగు", abbr: "తె" },
  { code: "hi", native: "हिन्दी", abbr: "हि" },
  { code: "ta", native: "தமிழ்", abbr: "தம" },
  { code: "kn", native: "ಕನ್ನಡ", abbr: "ಕನ" },
];

/* The top of both home lists: Sramika, then language, city and Help as real buttons -
   36px pills and a 40px call button. The language and the city used to be 10px words
   with a 13px tap area, the smallest things on the page for the two choices that
   change everything under them. */
/* On a laptop (lg, mock Laptop_1) the same header is the top bar across the page: the
   brand, the pages - Cars, Drivers, Trips, History - then language, city, the support
   number and Log in / Join. With it on the page the phone's bottom tab bar steps aside
   (html.has-topbar, index.css). A phone sees exactly the header it had. */
const BAR_X = "lg:px-[max(24px,calc((100vw_-_1240px)/2))]";

export default function Header({ city, onCityChange, onLocate, subtitle }) {
  const [open, setOpen] = useState(false);
  const [localCity, setLocalCity] = useState(city || "");
  const [langOpen, setLangOpen] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectErr, setDetectErr] = useState("");
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const { t, lang, setLang } = useLanguage();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const signedIn = !!(user || getAuth())?.phone;
  const PAGES = [["/", t("navCars")], ["/drivers", t("navHireDriver")], ["/trips", t("dk_trips")], ["/bookings", t("bookings")]];
  // The tab a page belongs to: a car and the cars' trip form are Cars, a driver and the
  // drivers' trip form are Drivers, every trip page is Trips.
  const activePage = pathname.startsWith("/trips") ? "/trips"
    : pathname.startsWith("/drivers") || pathname.startsWith("/hire-location") ? "/drivers"
    : pathname.startsWith("/bookings") ? "/bookings"
    : pathname === "/" || pathname.startsWith("/car/") || pathname.startsWith("/location-picker") ? "/" : "";
  useEffect(() => {
    document.documentElement.classList.add("has-topbar");
    return () => document.documentElement.classList.remove("has-topbar");
  }, []);

  // A city an admin added shows as soon as its list arrives (utils/cities addExtraCities).
  const [, setCitiesSeen] = useState(0);
  useEffect(() => {
    const again = () => setCitiesSeen((n) => n + 1);
    window.addEventListener(CITIES_EVENT, again);
    return () => window.removeEventListener(CITIES_EVENT, again);
  }, []);

  // Typed in any of the app's languages ("విజయవాడ" finds Vijayawada), or another name for it.
  const allFilteredCities = citiesMatching(citySearchQuery, [...CITY_LIST_TG, ...apOnlyCities()]);
  const current = HEADER_LANGS.find((l) => l.code === lang) || HEADER_LANGS[0];
  // The same number and the same rule as every other Help button: the placeholder
  // number is never offered as a way to reach anyone.
  const cfg = usePlatformConfig();
  const supportDigits = String(cfg?.supportPhoneNumber || "").replace(/\D/g, "").slice(-10);
  const hasSupport = supportDigits.length === 10 && supportDigits !== "9999999999";

  useEffect(() => { setLocalCity(city || ""); }, [city]);

  // "Change city" from anywhere on the page - an empty list, the no-city card.
  useEffect(() => {
    const openPicker = () => {
      setLangOpen(false);
      setOpen(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener(CITY_PICKER_EVENT, openPicker);
    return () => window.removeEventListener(CITY_PICKER_EVENT, openPicker);
  }, []);

  const choose = (c) => {
    setLocalCity(c);
    localStorage.setItem("sramika_selected_city", c);
    onCityChange?.(c);
    setOpen(false);
    setCitySearchQuery("");
  };

  // IMPORTANT: getCurrentPosition MUST be called synchronously inside the click
  // handler — no async/await or Promise wrapper — so the browser's "user gesture"
  // token is still active. This is required for the native OS permission popup to
  // appear on iOS Safari and Android Chrome.
  function handleDetect() {
    if (!navigator.geolocation) {
      setDetectErr(t("hm_locNotSupported"));
      return;
    }

    setDetecting(true);
    setDetectErr("");

    // Maps loads on demand now (utils/googleMaps); start it with the location request.
    loadGoogleMaps().catch(() => {});
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await loadGoogleMaps().catch(() => {});
        if (!window.google?.maps?.Geocoder) {
          setDetecting(false);
          setDetectErr(t("hm_locMapsOff"));
          return;
        }
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
          (results, status) => {
            setDetecting(false);
            if (status !== "OK" || !results?.length) {
              setDetectErr(t("hm_locNotFound"));
              return;
            }
            const comp = results[0].address_components || [];
            const locality =
              comp.find((c) => c.types.includes("locality"))?.long_name ||
              comp.find((c) => c.types.includes("administrative_area_level_2"))?.long_name || "";
            // Google names the place in the phone's language - "విజయవాడ" is Vijayawada.
            const matchedCity = canonicalCity(locality) || locality;
            if (matchedCity) choose(matchedCity);
            else setDetectErr(t("hm_locNoCity"));
            /* The spot itself becomes the pickup (31 Oct 2026, REQ-0004/0010/0011): the list
               is chosen by distance from it, so a customer in Avanigadda or Nuzvid - towns
               no driver names as their city - sees every car and driver that reaches them. */
            if (onLocate) onLocate({ value: results[0].formatted_address || locality, location: { lat: pos.coords.latitude, lng: pos.coords.longitude }, selected: true });
          }
        );
      },
      (err) => {
        setDetecting(false);
        if (err.code === 1) setDetectErr(t("hm_locDenied"));           // PERMISSION_DENIED
        else if (err.code === 2) setDetectErr(t("hm_locUnavailable"));  // POSITION_UNAVAILABLE
        else setDetectErr(t("hm_locTimeout"));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  const PILL = "h-[36px] px-[8px] rounded-full border border-[#E2E8F0] bg-white flex items-center gap-[4px] text-[10px] font-bold text-[#1F2937] active:scale-[0.97] transition-colors";

  return (
    <div className="relative pt-1 lg:pt-0 lg:h-[64px]">
      <div className={`flex items-center gap-[6px] bg-white/95 rounded-[16px] border border-[var(--color-border)] shadow-[0_4px_14px_rgba(15,23,42,0.04)] px-2.5 py-2 min-h-[58px]
        lg:fixed lg:top-0 lg:inset-x-0 lg:z-40 lg:h-[64px] lg:py-0 lg:gap-3 lg:rounded-none lg:border-0 lg:border-b lg:border-[#E5E7EB] lg:bg-white lg:shadow-[0_1px_10px_rgba(15,23,42,0.05)] ${BAR_X}`}>
        {/* The brand keeps its width - "Sramika" is one word and cannot wrap - and the
            city button gives way instead, shortening the name with an ellipsis. The
            line under the name is sized out of the brand's width (w-0 min-w-full), so
            it wraps rather than pushing the buttons off a narrow phone. */}
        <Link to="/" className="flex-1 lg:flex-none flex items-center gap-[7px]">
          <img src="/logo-160.png" alt="Sramika" className="w-[38px] h-[38px] object-contain shrink-0" />
          <div className="flex-1 flex flex-col justify-center gap-0.5">
            <h1 className="text-[16px] font-extrabold text-[#7BAE00] leading-none whitespace-nowrap" style={{ letterSpacing: "-0.2px" }}>{t("appName")}</h1>
            <p className="w-0 min-w-full text-[9px] lg:text-[11px] font-medium text-[#64748B] leading-[1.15] line-clamp-2">{subtitle || t("appSubtitle")}</p>
          </div>
        </Link>

        {/* The pages, on a laptop. */}
        <nav className="hidden lg:flex items-center gap-6 ml-8 h-full">
          {PAGES.map(([to, word]) => (
            <Link key={to} to={to}
              className={`h-full flex items-center text-[14.5px] font-bold border-b-[3px] transition-colors ${activePage === to ? "text-[#3F5E00] border-[#7BAE00]" : "text-[#334155] border-transparent hover:text-[#3F5E00]"}`}>
              {word}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-[4px] lg:gap-2 min-w-0 lg:ml-auto">
          <div className="relative shrink-0">
            <button type="button" onClick={() => { setLangOpen((p) => !p); setOpen(false); }}
              aria-label={t("hm_changeLang")} aria-expanded={langOpen} className={PILL}>
              {/* The globe says "language" in any script; no arrow, so the city keeps
                  room for its whole name. */}
              <Icon name="globe" size={15} stroke="#5E8700" strokeWidth={2.2} />
              <span className="lg:hidden">{current.abbr}</span>
              <span className="hidden lg:inline">{current.native}</span>
            </button>

            {langOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                <div className="absolute top-full right-0 z-50 mt-2 py-1 w-[168px] rounded-2xl bg-white border border-[#E5E7EB] shadow-xl fade-up">
                  {HEADER_LANGS.map((l) => (
                    <button key={l.code} type="button" onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full min-h-[44px] px-3 flex items-center justify-between text-left text-[12px] font-semibold ${
                        lang === l.code ? "text-[#3F5E00] bg-[#F1F7E3]" : "text-[#1F2937]"}`}>
                      <span>{l.native}</span>
                      {lang === l.code && <Icon name="check" size={14} stroke="#5E8700" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button type="button" onClick={() => { setOpen((p) => !p); setLangOpen(false); }}
            aria-label={t("hm_changeCity")} aria-expanded={open} className={`${PILL} min-w-0 max-w-[160px]`}>
            <Icon name="mapPin" size={14} stroke="#5E8700" strokeWidth={2.2} />
            <span className="min-w-0 truncate">{localCity ? cityIn(localCity, lang) : t("hm_chooseCity")}</span>
          </button>

          {hasSupport && (
            <a href={`tel:+91${supportDigits}`} aria-label={t("hm_helpCall")} title={t("hm_helpCall")}
              className="lg:hidden shrink-0 w-[40px] h-[40px] rounded-full bg-white border border-[#CFE3A0] text-[#5E8700] flex items-center justify-center shadow-sm active:scale-95">
              <Icon name="phone" size={18} strokeWidth={2.1} />
            </a>
          )}
          {/* On a laptop: the number itself, and Log in / Join (or the account). */}
          {hasSupport && (
            <a href={`tel:+91${supportDigits}`} className={`${PILL} hidden lg:flex`}>
              <Icon name="phone" size={14} stroke="#5E8700" strokeWidth={2.2} />
              <span>{t("cb_pill")} · {supportDigits.slice(0, 5)} {supportDigits.slice(5)}</span>
            </a>
          )}
          <Link to={signedIn ? "/account" : "/login"}
            className="hidden lg:flex h-[38px] px-4 rounded-[10px] bg-[#7BAE00] hover:bg-[#6E9D00] text-white text-[13px] font-extrabold items-center whitespace-nowrap">
            {signedIn ? t("account") : t("hm_loginJoin")}
          </Link>
        </div>
      </div>

      {/* The city list */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 right-0 z-50 mt-2 p-3 rounded-2xl bg-white border border-[#E5E7EB] shadow-xl fade-up lg:fixed lg:top-[70px] lg:left-auto lg:right-[max(24px,calc((100vw_-_1240px)/2))] lg:w-[380px]">
            <p className="text-[11px] font-extrabold text-[#0F172A] mb-2">{t("hm_chooseCity")}</p>

            {/* Tap-to-detect: the permission prompt only appears inside this tap. */}
            <button type="button" onClick={handleDetect} disabled={detecting}
              className="w-full min-h-[44px] px-3 rounded-xl border border-dashed border-[#B9D77A] bg-[#F8FBF1] flex items-center gap-2 text-[11px] font-semibold text-[#3F5E00] disabled:opacity-70">
              {detecting
                ? <span className="w-3.5 h-3.5 border-2 border-[#7BAE00] border-t-transparent rounded-full animate-spin shrink-0" />
                : <Icon name="navigation" size={15} stroke="#5E8700" strokeWidth={2.2} />}
              {detecting ? t("hm_finding") : t("hm_useMyLocation")}
            </button>
            {detectErr && <p className="mt-2 text-[10px] font-medium text-[#DC3545] leading-snug">{detectErr}</p>}

            <div className="relative mt-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none">
                <Icon name="search" size={15} strokeWidth={2.2} />
              </span>
              <input type="text" value={citySearchQuery} autoFocus
                onChange={(e) => setCitySearchQuery(e.target.value)}
                placeholder={t("hm_typeCity")} aria-label={t("hm_typeCity")}
                className="w-full h-[44px] pl-9 pr-3 rounded-xl border border-[#E5E7EB] bg-white text-[11.5px] font-medium text-[#1F2937] outline-none focus:border-[#7BAE00]" />
            </div>

            <div className="mt-2 max-h-[220px] overflow-y-auto overscroll-contain">
              {allFilteredCities.length > 0 ? (
                allFilteredCities.map((c) => (
                  <button key={c} type="button" onClick={() => choose(c)}
                    className={`w-full min-h-[44px] px-3 rounded-xl flex items-center justify-between text-left text-[11.5px] font-semibold ${
                      localCity === c ? "bg-[#F1F7E3] text-[#3F5E00]" : "text-[#334155]"}`}>
                    <span>{cityIn(c, lang)}</span>
                    {localCity === c && <Icon name="check" size={14} stroke="#5E8700" strokeWidth={3} />}
                  </button>
                ))
              ) : (
                <p className="text-center py-4 text-[11px] text-[#94A3B8]">{t("hm_noCities")}</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
